import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import md5 from 'crypto-js/md5';
import { useCallback, useEffect, useRef } from 'react';

export function useTrendEyeglassesTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  const enterTimer = useRef<number>(Date.now());
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });

  // 生成活动标识 MD5
  const marketActivityId = md5('SpringSale').toString();

  /**
   * 种草会场页面浏览
   */
  const trackPageView = useCallback(() => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    enterTimer.current = Date.now();

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_View',
          properties: {
            page_name: '202603春促种草主会场',
            refer_page_name,
            refer_url_path,
            market_activity_id: marketActivityId,
            market_activity_name: '26年3月春促活动',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Trend Eyeglasses page view tracking error:', error);
    }
  }, [trackEvent, getFromRouterInfo]);

  /**
   * 种草会场页面离开
   */
  const trackPageLeave = useCallback(() => {
    const { viewAdvance, viewArea } = lastScrollState.current;

    try {
      trackEvent(
        {
          eventName: 'CampaignPage_Leave',
          properties: {
            page_name: '202603春促种草主会场',
            view_advance: viewAdvance,
            view_duration: Math.ceil((Date.now() - enterTimer.current) / 1000),
            view_area: viewArea,
            market_activity_id: marketActivityId,
            market_activity_name: '26年3月春促活动',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Trend Eyeglasses page leave tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * 商品点击
   */
  const trackProductClick = useCallback(
    (item: any, index: number, moduleName: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties: {
              page_name: '202603春促种草主会场',
              module_name: moduleName,
              pits_index: index,
              product_id: `${item.id}`,
              product_name: item.short_name || item.name,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_price: parseFloat(nowPriceInfo.price || '0'),
              current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
              base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
              dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
              currency: currency?.name || '',
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Trend Eyeglasses product click tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * 商品曝光
   */
  const trackProductExposure = useCallback(
    (item: any, index: number, moduleName: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties: {
              page_name: '202603春促种草主会场',
              module_name: moduleName,
              pits_index: index,
              product_id: `${item.id}`,
              product_name: item.short_name || item.name,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_price: parseFloat(nowPriceInfo.price || '0'),
              current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
              base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
              dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
              currency: currency?.name || '',
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Trend Eyeglasses product exposure tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  // 定期更新滚动状态
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        const viewArea = getViewArea();
        lastScrollState.current = { viewAdvance, viewArea };
      } catch (error) {
        logger.error('Error updating scroll state:', error);
      }
    };

    updateScrollState();

    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    trackPageView,
    trackPageLeave,
    trackProductClick,
    trackProductExposure,
  };
}
